import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * SettingsStatusPill — the v7 user-status pill (SCRUM-457).
 *
 * Token-faithful port of the prototype `.settings-status` (app-shell 7.html):
 * an inline mono pill, 0.625rem / 700 / letter-spacing 0.04em, padding 2px 8px,
 * rounded --r-full. Three variants matching the prototype's `statusLabel`:
 *   - active      → --success text on color-mix(--success 14%, transparent)
 *   - pending     → --warning text on color-mix(--warning 16%, transparent)
 *   - deactivated → --text-3 text on --bg-2
 *
 * The 14%/16% mixes are reproduced verbatim with color-mix arbitrary values
 * (the --success-dim/--warning-dim tokens are 12%, not a match). The label text
 * (Active/Pending/Deactivated) is rendered as-is — the pill does NOT uppercase;
 * the prototype passes already-capitalized labels.
 */
export type UserStatus = 'active' | 'pending' | 'deactivated';

const STATUS_LABEL: Readonly<Record<UserStatus, string>> = {
  active: 'Active',
  pending: 'Pending',
  deactivated: 'Deactivated',
};

const STATUS_CLASS: Readonly<Record<UserStatus, string>> = {
  active: 'text-success bg-[color-mix(in_srgb,var(--success)_14%,transparent)]',
  pending: 'text-warning bg-[color-mix(in_srgb,var(--warning)_16%,transparent)]',
  deactivated: 'text-text-3 bg-bg-2',
};

export type SettingsStatusPillProps = React.ComponentPropsWithoutRef<'span'> & {
  status: UserStatus;
};

export const SettingsStatusPill = React.forwardRef<HTMLSpanElement, SettingsStatusPillProps>(
  function SettingsStatusPill({ status, className, children, ...rest }, ref) {
    return (
      <span
        ref={ref}
        className={cn(
          'rounded-tk-full inline-block px-2 py-[2px] font-mono text-[0.625rem] font-bold tracking-[0.04em]',
          STATUS_CLASS[status],
          className,
        )}
        {...rest}
      >
        {children ?? STATUS_LABEL[status]}
      </span>
    );
  },
);
